<template>
  <FormattedMessage
    v-if="estimatedDeliveryDate"
    data-test="delivery-date"
    :definition="translations.singleDeliveryDate"
  >
    <template #weekday>
      <template v-if="isEstimatedDeliveryToday">
        {{ i18n(translations.today) }}
      </template>
      <template v-else>
        {{ i18n.date(new Date(estimatedDeliveryDate), { weekday: 'long' }) }}
      </template>
    </template>
    <template #month>
      {{ i18n.date(new Date(estimatedDeliveryDate), { month: 'short' }) }}
    </template>
    <template #day>
      {{ i18n.date(new Date(estimatedDeliveryDate), { day: 'numeric' }) }}
    </template>
  </FormattedMessage>
  <FormattedMessage
    v-else-if="isPromisedDateRangeOnSameMonth"
    data-test="delivery-date"
    :definition="translations.sameMonthDeliveryDateRange"
  >
    <template #month>
      {{ i18n.date(new Date(promisedMinDeliveryDate), { month: 'short' }) }}
    </template>
    <template #day1>
      {{ i18n.date(new Date(promisedMinDeliveryDate), { day: 'numeric' }) }}
    </template>
    <template #day2>
      {{ i18n.date(new Date(promisedMaxDeliveryDate), { day: 'numeric' }) }}
    </template>
  </FormattedMessage>
  <FormattedMessage
    v-else
    data-test="delivery-date"
    :definition="translations.splitMonthDeliveryDateRange"
  >
    <template #month1>
      {{ i18n.date(new Date(promisedMinDeliveryDate), { month: 'short' }) }}
    </template>
    <template #month2>
      {{ i18n.date(new Date(promisedMaxDeliveryDate), { month: 'short' }) }}
    </template>
    <template #day1>
      {{ i18n.date(new Date(promisedMinDeliveryDate), { day: 'numeric' }) }}
    </template>
    <template #day2>
      {{ i18n.date(new Date(promisedMaxDeliveryDate), { day: 'numeric' }) }}
    </template>
  </FormattedMessage>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { DateTime } from '@backmarket/http-api/src/standards/DateTime'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import translations from './DeliveryDate.translations'

const i18n = useI18n()

const {
  promisedMinDeliveryDate,
  promisedMaxDeliveryDate,
  estimatedDeliveryDate,
} = defineProps<{
  promisedMinDeliveryDate: DateTime
  promisedMaxDeliveryDate: DateTime
  estimatedDeliveryDate: DateTime | null
}>()

const isEstimatedDeliveryToday = computed(() => {
  if (!estimatedDeliveryDate) {
    return false
  }
  const today = new Date()
  const estimatedDelivery = new Date(estimatedDeliveryDate)

  return (
    estimatedDelivery.getDate() === today.getDate() &&
    estimatedDelivery.getMonth() === today.getMonth() &&
    estimatedDelivery.getFullYear() === today.getFullYear()
  )
})

const isPromisedDateRangeOnSameMonth = computed(() => {
  const minDate = new Date(promisedMinDeliveryDate)
  const maxDate = new Date(promisedMaxDeliveryDate)

  return (
    minDate.getMonth() === maxDate.getMonth() &&
    minDate.getFullYear() === maxDate.getFullYear()
  )
})
</script>
